import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const VerifyPage: React.FC = () => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { verifyOTP, pendingEmail, login } = useAuth();
  const navigate = useNavigate();

  // Redirect to login if no pending email
  React.useEffect(() => {
    if (!pendingEmail) {
      navigate('/login');
    }
  }, [pendingEmail, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!otp) {
      setError('Please enter the verification code');
      return;
    }

    setIsSubmitting(true);

    const result = await verifyOTP(otp);

    setIsSubmitting(false);

    if (result.success) {
      // Navigate to landing page
      navigate('/');
    } else {
      setError(result.error || 'Invalid verification code');
    }
  };

  const handleResendOTP = async () => {
    if (!pendingEmail) return;

    const result = await login(pendingEmail);
    if (result.success) {
      setResendMessage('OTP sent successfully!');
      setTimeout(() => setResendMessage(''), 3000);
    } else {
      setError(result.error || 'Failed to resend OTP');
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-orange-50 to-amber-100">
      {/* Header with Persist Logo */}
      <div className="p-6">
        <img
          src="/assets/persist-logo-black.avif"
          alt="Persist AI"
          className="h-10 w-auto"
        />
      </div>

      {/* Centered Verify Card */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold text-center text-[#ff6600]">
              Verify Your Email
            </CardTitle>
            <CardDescription className="text-center">
              Enter the verification code sent to {pendingEmail}
            </CardDescription>
          </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">Verification Code</Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
                maxLength={8}
                className={error ? 'border-red-500 text-center text-2xl tracking-widest' : 'text-center text-2xl tracking-widest'}
                disabled={isSubmitting}
              />
              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}
              {resendMessage && (
                <p className="text-sm text-green-600">{resendMessage}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Verifying...' : 'Verify'}
            </Button>

            <div className="flex justify-between items-center text-sm">
              <Button
                type="button"
                variant="link"
                onClick={handleResendOTP}
                className="p-0 h-auto"
                disabled={isSubmitting}
              >
                Resend OTP
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={handleBackToLogin}
                className="p-0 h-auto"
              >
                Back to Login
              </Button>
            </div>

            <div className="text-center text-xs text-muted-foreground mt-4 p-3 bg-orange-50 rounded">
              <p>Check your email for the verification code</p>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default VerifyPage;
